const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api";

function authHeaders() {
  const token = localStorage.getItem("erp_auth_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Upload Resume File
export async function uploadResume(file) {
  const token = localStorage.getItem("erp_auth_token");
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_BASE}/ai-resume/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to upload resume");
  return data.data;
}

// Parse Resume and Extract Information
export async function parseResume({ resumeId, filePath }) {
  const res = await fetch(`${API_BASE}/ai-resume/parse`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ resumeId, filePath }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to parse resume");
  return data.data;
}

// Analyze Resume - Get scores and insights
export async function analyzeResume({ resumeId, parsedData }) {
  const res = await fetch(`${API_BASE}/ai-resume/analyze`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ resumeId, parsedData }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to analyze resume");
  return data.data;
}

// Get Resume Health Score
export async function getResumeHealth({ resumeId }) {
  const res = await fetch(`${API_BASE}/ai-resume/${resumeId}/health`, {
    method: "GET",
    headers: authHeaders(),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to get resume health");
  return data.data;
}

// Get ATS Score
export async function getATSScore({ resumeId, jobDescription = null }) {
  const res = await fetch(`${API_BASE}/ai-resume/${resumeId}/ats-score`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ jobDescription }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to get ATS score");
  return data.data;
}

// AI Chat - Send message and get response
export async function resumeChat({ resumeId, message, conversationHistory = [] }) {
  const res = await fetch(`${API_BASE}/ai-resume/${resumeId}/chat`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ resumeId, message, conversationHistory }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to send message");
  return data.data;
}

// Improve Resume Section
export async function improveSection({ resumeId, section, content }) {
  const res = await fetch(`${API_BASE}/ai-resume/${resumeId}/improve`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ resumeId, section, content }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to improve section");
  return data.data;
}

// Job Match Analysis
export async function matchJobDescription({ resumeId, jobDescription }) {
  const res = await fetch(`${API_BASE}/ai-resume/${resumeId}/job-match`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ resumeId, jobDescription }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to match job");
  return data.data;
}

// Get Resume Versions
export async function getResumeVersions({ resumeId }) {
  const res = await fetch(`${API_BASE}/ai-resume/${resumeId}/versions`, {
    method: "GET",
    headers: authHeaders(),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to get versions");
  return data.data;
}

// Save Resume Version
export async function saveResumeVersion({ resumeId, parsedData, analysis, message = "Auto-saved" }) {
  const res = await fetch(`${API_BASE}/ai-resume/${resumeId}/save-version`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ parsedData, analysis, message }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to save version");
  return data.data;
}

// Get Resume List
export async function getMyResumes() {
  const res = await fetch(`${API_BASE}/ai-resume`, {
    method: "GET",
    headers: authHeaders(),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to get resumes");
  return data.data;
}

// Get Single Resume
export async function getResume({ resumeId }) {
  const res = await fetch(`${API_BASE}/ai-resume/${resumeId}`, {
    method: "GET",
    headers: authHeaders(),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to get resume");
  return data.data;
}

// Delete Resume
export async function deleteResume({ resumeId }) {
  const res = await fetch(`${API_BASE}/ai-resume/${resumeId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to delete resume");
  return data.data;
}

// Generate Interview Questions from Resume
export async function generateInterviewQuestions({ resumeId, type = "technical" }) {
  const res = await fetch(`${API_BASE}/ai-resume/${resumeId}/interview-questions`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ resumeId, type }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Failed to generate questions");
  return data.data;
}